import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRoomNoteDialogStore } from '../../store/roomNoteDialogStore';
import { useUserRightsStore } from '../../store/userRightsStore';
import { GbApiUserRightEnum } from '../../enums/GbApiUserRightEnum';
import { RoomNoteSaveErrorEnum } from '../../enums/RoomNoteSaveErrorEnum';
import { ROOM_NOTE_MAX_LENGTH, saveRoomNote } from '../../logic/saveRoomNote';
import './RoomNoteDialogComponent.css';
import { CloseIcon } from '../Icons';

const errorKeys: Record<RoomNoteSaveErrorEnum, string> = {
    [RoomNoteSaveErrorEnum.Validation]:   'ui.roomNote.dialog.error.validation',
    [RoomNoteSaveErrorEnum.Unauthorized]: 'ui.roomNote.dialog.error.unauthorized',
    [RoomNoteSaveErrorEnum.Forbidden]:    'ui.roomNote.dialog.error.forbidden',
    [RoomNoteSaveErrorEnum.NotFound]:     'ui.roomNote.dialog.error.notFound',
    [RoomNoteSaveErrorEnum.Failed]:       'ui.roomNote.dialog.error.failed',
};

const RoomNoteDialogComponent = () => {
    const { t } = useTranslation();
    const { isOpen, globalId, roomName, note, close } = useRoomNoteDialogStore();
    const canEdit = useUserRightsStore((s) => s.rights[GbApiUserRightEnum.RoomNotesEdit] === true);
    const [editedNote, setEditedNote] = useState(note);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<RoomNoteSaveErrorEnum | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const openCountRef = useRef(0);

    // Every open starts from the stored note, so closing discards edits
    useEffect(() => {
        if (isOpen) {
            openCountRef.current++;
            setEditedNote(note);
            setIsSaving(false);
            setError(null);
            setTimeout(() => textareaRef.current?.focus(), 0);
        }
    }, [isOpen, globalId, note]);

    if (!isOpen) {
        return null;
    }

    const isTooLong = editedNote.trim().length > ROOM_NOTE_MAX_LENGTH;
    // Notes are saved trimmed, so whitespace-only changes would save nothing
    const isUnchanged = editedNote.trim() === note.trim();

    const handleSave = async () => {
        const openCount = openCountRef.current;
        setIsSaving(true);
        setError(null);

        // Stores a successful save even if the dialog is closed meanwhile
        const result = await saveRoomNote(globalId, editedNote);

        // The dialog may have been closed, or reopened for another room, while saving
        if (openCount !== openCountRef.current || !useRoomNoteDialogStore.getState().isOpen) return;

        setIsSaving(false);
        if (result.ok) {
            close();
        } else {
            setError(result.error); // Keeps the user's text so they can retry
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            close();
        }
    };

    return (
        <div className="room-note-dialog-overlay" onClick={close}>
            <div
                className="room-note-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="room-note-dialog-title"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
            >
                <div className="room-note-dialog-header">
                    <div className="room-note-dialog-header-left">
                        <span className="room-note-dialog-status-dot" />
                        <span id="room-note-dialog-title" className="room-note-dialog-title">{roomName}</span>
                    </div>
                    <button className="room-note-dialog-close" onClick={close} aria-label={t('common.close')}>
                        <CloseIcon />
                    </button>
                </div>

                <div className="room-note-dialog-body">
                    <div className="room-note-dialog-section-title">
                        {t('ui.roomNote.dialog.noteSection')}
                    </div>
                    <textarea
                        ref={textareaRef}
                        rows={5}
                        value={editedNote}
                        readOnly={!canEdit}
                        placeholder={canEdit ? t('ui.roomNote.dialog.notePlaceholder') : undefined}
                        aria-label={t('ui.roomNote.dialog.noteSection')}
                        aria-invalid={isTooLong}
                        onChange={(e) => setEditedNote(e.target.value)}
                    />
                    {canEdit && (
                        <div className={`room-note-dialog-counter${isTooLong ? ' over-limit' : ''}`} data-testid="room-note-counter">
                            {editedNote.trim().length} / {ROOM_NOTE_MAX_LENGTH}
                        </div>
                    )}
                    {error && (
                        <div className="room-note-dialog-error" role="alert">
                            {t(errorKeys[error])}
                        </div>
                    )}
                </div>

                <div className="room-note-dialog-footer">
                    <button className="room-note-dialog-btn-cancel" onClick={close}>
                        {canEdit ? t('common.cancel') : t('common.close')}
                    </button>
                    {canEdit && (
                        <button
                            className="room-note-dialog-btn-primary"
                            disabled={isSaving || isUnchanged || isTooLong}
                            onClick={handleSave}
                        >
                            {isSaving ? t('ui.roomNote.dialog.saving') : t('common.save')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default RoomNoteDialogComponent;
